"use client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createSupabaseBrowserClient } from "../lib/auth/supabase-browser";

interface SignupInput {
  email: string;
  password: string;
  full_name?: string;
  username?: string;
  phone_number?: string;
  avatar_url?: string;
}

interface SignupResult {
  userId: string | null;
  profileId?: string;
  created?: boolean;
  error?: string;
}

async function signupAndCreateProfile(
  input: SignupInput
): Promise<SignupResult> {
  const supabase = createSupabaseBrowserClient();
  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
  });
  if (error) {
    throw new Error(error.message);
  }
  const accessToken = data.session?.access_token;
  const profileRes = await fetch("/api/auth/profile", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      full_name: input.full_name?.trim() || undefined,
      username: input.username?.trim() || undefined,
      phone_number: input.phone_number?.trim() || undefined,
      avatar_url: input.avatar_url?.trim() || undefined,
    }),
  });
  const profileJson = await profileRes.json();
  if (!profileRes.ok) {
    throw new Error(profileJson.error || "Profile creation failed");
  }
  return {
    userId: data.user?.id || null,
    profileId: profileJson.profileId,
    created: profileJson.created,
  };
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: signupAndCreateProfile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}
